import React, { useState, useEffect } from "react";
import moment from "moment";

const OverallSeedCalls = ({
  doctor,
  callListId,
  availableMonths,
  medianValues,
  onApply,
  onClose,
  existingArtificialCalls,
}) => {
  const [monthlyArtificialCalls, setMonthlyArtificialCalls] = useState({});
  const [totals, setTotals] = useState({
    regularWeekdays: 0,
    weekends: 0,
    publicHolidays: 0,
  });

  useEffect(() => {
    // Initialize monthly artificial calls with existing values
    const initialMonthly = {};
    availableMonths.forEach((month) => {
      if (month !== "overall") {
        const monthFormatted = moment(month, "MMM YYYY").format("YYYY-MM");
        const existingCall = existingArtificialCalls?.find(
          (call) => call.yearMonth === monthFormatted
        );

        initialMonthly[month] = {
          regularWeekdays: existingCall?.regularWeekdays || 0,
          weekends: existingCall?.weekends || 0,
          publicHolidays: existingCall?.publicHolidays || 0,
        };
      }
    });
    setMonthlyArtificialCalls(initialMonthly);
  }, [availableMonths, existingArtificialCalls]);

  useEffect(() => {
    // Calculate totals whenever monthly values change
    const newTotals = {
      regularWeekdays: 0,
      weekends: 0,
      publicHolidays: 0,
    };

    Object.values(monthlyArtificialCalls).forEach((month) => {
      newTotals.regularWeekdays += month.regularWeekdays;
      newTotals.weekends += month.weekends;
      newTotals.publicHolidays += month.publicHolidays;
    });

    setTotals(newTotals);
  }, [monthlyArtificialCalls]);

  const handleValueChange = (month, field, value) => {
    setMonthlyArtificialCalls((prev) => ({
      ...prev,
      [month]: {
        ...prev[month],
        [field]: parseInt(value) || 0,
      },
    }));
  };

  const handleCatchUpToMedian = () => {
    const monthCount = Object.keys(monthlyArtificialCalls).length;
    if (monthCount === 0) return;

    // Calculate how many calls we need to add to reach median
    const callsNeededToReachMedian = {
      regularWeekdays: Math.max(
        0,
        medianValues.regularWeekdays - doctor.regularWeekdays
      ),
      weekends: Math.max(0, medianValues.weekends - doctor.weekends),
      publicHolidays: Math.max(
        0,
        medianValues.publicHolidays - doctor.publicHolidays
      ),
    };

    // Calculate base distribution per month (floor division)
    const baseDistribution = {
      regularWeekdays: Math.floor(
        callsNeededToReachMedian.regularWeekdays / monthCount
      ),
      weekends: Math.floor(callsNeededToReachMedian.weekends / monthCount),
      publicHolidays: Math.floor(
        callsNeededToReachMedian.publicHolidays / monthCount
      ),
    };

    // Calculate remainders to distribute
    const remainders = {
      regularWeekdays: callsNeededToReachMedian.regularWeekdays % monthCount,
      weekends: callsNeededToReachMedian.weekends % monthCount,
      publicHolidays: callsNeededToReachMedian.publicHolidays % monthCount,
    };

    // Sort months from newest to oldest
    const sortedMonths = Object.keys(monthlyArtificialCalls).sort((a, b) =>
      moment(b, "MMM YYYY").diff(moment(a, "MMM YYYY"))
    );

    // Distribute calls evenly across months
    const newMonthlyValues = {};
    sortedMonths.forEach((month, index) => {
      newMonthlyValues[month] = {
        regularWeekdays:
          baseDistribution.regularWeekdays +
          (index < remainders.regularWeekdays ? 1 : 0),
        weekends:
          baseDistribution.weekends + (index < remainders.weekends ? 1 : 0),
        publicHolidays:
          baseDistribution.publicHolidays +
          (index < remainders.publicHolidays ? 1 : 0),
      };
    });

    setMonthlyArtificialCalls(newMonthlyValues);
  };

  const handleApply = async () => {
    try {
      // Convert months to an array of updates for the API
      const updates = Object.entries(monthlyArtificialCalls).map(
        ([month, values]) => ({
          doctorId: doctor.doctorId,
          callListId,
          yearMonth: moment(month, "MMM YYYY").format("YYYY-MM"),
          ...values,
        })
      );

      // Pass all updates to the parent handler at once
      await onApply(updates);

      // Close the modal after all updates are processed
      onClose();
    } catch (error) {
      console.error("Error applying overall catch-up calls:", error);
      alert("Failed to apply changes. Please try again.");
    }
  };

  // Calculate overall totals (real + artificial)
  const overallTotals = {
    regularWeekdays: doctor.regularWeekdays + totals.regularWeekdays,
    weekends: doctor.weekends + totals.weekends,
    publicHolidays: doctor.publicHolidays + totals.publicHolidays,
  };

  return (
    <div className="catch-up-modal">
      <div className="catch-up-modal-content overall-seed-calls">
        <div className="catch-up-modal-header">
          <h3>Seed Artificial Calls for {doctor.doctorAbbreviation}</h3>
          <button className="catch-up-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="catch-up-form">
          <div className="catch-up-header">
            <div></div>
            <div>Total</div>
            <div>Median</div>
          </div>

          <div className="catch-up-form-row">
            <label>Regular Weekdays:</label>
            <div className="dynamic-total">{overallTotals.regularWeekdays}</div>
            <div className="median-value">{medianValues.regularWeekdays}</div>
          </div>

          <div className="catch-up-form-row">
            <label>Weekends:</label>
            <div className="dynamic-total">{overallTotals.weekends}</div>
            <div className="median-value">{medianValues.weekends}</div>
          </div>

          <div className="catch-up-form-row">
            <label>Public Holidays:</label>
            <div className="dynamic-total">{overallTotals.publicHolidays}</div>
            <div className="median-value">{medianValues.publicHolidays}</div>
          </div>

          <button
            className="catch-up-button give-median"
            onClick={handleCatchUpToMedian}
          >
            Catch up to median
          </button>

          <div className="monthly-sections">
            {Object.keys(monthlyArtificialCalls)
              .sort((a, b) => moment(b, "MMM YYYY").diff(moment(a, "MMM YYYY")))
              .map((month) => (
                <div key={month} className="monthly-seed-calls">
                  <h4>{month}</h4>
                  <div className="catch-up-form-row">
                    <label>Regular Weekdays:</label>
                    <div className="div-number-input-group">
                      <button
                        onClick={() =>
                          handleValueChange(
                            month,
                            "regularWeekdays",
                            monthlyArtificialCalls[month].regularWeekdays - 1
                          )
                        }
                        disabled={
                          monthlyArtificialCalls[month].regularWeekdays <= 0
                        }
                      >
                        −
                      </button>
                      <input
                        type="text"
                        value={monthlyArtificialCalls[month].regularWeekdays}
                        onChange={(e) =>
                          handleValueChange(
                            month,
                            "regularWeekdays",
                            e.target.value
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          handleValueChange(
                            month,
                            "regularWeekdays",
                            monthlyArtificialCalls[month].regularWeekdays + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="catch-up-form-row">
                    <label>Weekends:</label>
                    <div className="div-number-input-group">
                      <button
                        onClick={() =>
                          handleValueChange(
                            month,
                            "weekends",
                            monthlyArtificialCalls[month].weekends - 1
                          )
                        }
                        disabled={monthlyArtificialCalls[month].weekends <= 0}
                      >
                        −
                      </button>
                      <input
                        type="text"
                        value={monthlyArtificialCalls[month].weekends}
                        onChange={(e) =>
                          handleValueChange(month, "weekends", e.target.value)
                        }
                      />
                      <button
                        onClick={() =>
                          handleValueChange(
                            month,
                            "weekends",
                            monthlyArtificialCalls[month].weekends + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="catch-up-form-row">
                    <label>Public Holidays:</label>
                    <div className="div-number-input-group">
                      <button
                        onClick={() =>
                          handleValueChange(
                            month,
                            "publicHolidays",
                            monthlyArtificialCalls[month].publicHolidays - 1
                          )
                        }
                        disabled={
                          monthlyArtificialCalls[month].publicHolidays <= 0
                        }
                      >
                        −
                      </button>
                      <input
                        type="text"
                        value={monthlyArtificialCalls[month].publicHolidays}
                        onChange={(e) =>
                          handleValueChange(
                            month,
                            "publicHolidays",
                            e.target.value
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          handleValueChange(
                            month,
                            "publicHolidays",
                            monthlyArtificialCalls[month].publicHolidays + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          <div className="catch-up-actions">
            <button className="catch-up-button" onClick={handleApply}>
              Apply All Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallSeedCalls;
